<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4">
    <div style="max-width:597px">
        <h3>{{ useManualPickupOrDeliveryTime ? 'Endre tidligst hentetid' : 'Tidligst hentetid' }}</h3>
        <p class="mt-4">Hentetid er den tiden det tar fra en bestilling kommer inn til den blir hentet.</p>

        <div v-if="hasMultiLocations()" class="select-location-box mt-4">
          <span class="ingredient-title">Velg avdeling</span>
          <select class="form-select" v-model="locationId">
            <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
          </select>
        </div>

        <!-- Manual Pickup Time: button grid (old UI) -->
        <div v-if="useManualPickupOrDeliveryTime" class="mt-4 mb-5">
          <span class="ingredient-title">Velg tidligst hentetid</span>

          <button v-for="minutes in pickupTimeOptions" :key="minutes" type="button" :class="isCurrent(minutes) ? 'earliestPickupTime_btn_selected' : 'earliestPickupTime_btn'" @click="setEarliestPickupTime(minutes)"> {{ formatPickupTime(minutes) }} <span v-if="earliestPickupTime == minutes" class="check ms-2"></span></button>
        </div>

        <!-- Smart Pickup Time configuration -->
        <div v-else>
          <div class="mt-4 smart-pickup-section">
            <span class="ingredient-title">Smart beregning av hentetid</span>
            <p class="text-muted small mt-1">
              Hentetiden justeres automatisk basert på hvor mange bestillinger kjøkkenet jobber med.
              Produkter med tilberedningstid bruker den direkte. Andre produkter bruker pris som estimat.
              Du kan justere beregningen ved å endre verdiene under.
            </p>

            <div class="smart-pickup-config mt-3 p-3 border rounded bg-body-tertiary">
              <div class="mb-3">
                <label class="form-label fw-bold">Minimums hentetid (minutter)</label>
                <p class="text-muted small">Hentetiden når det ikke er noen bestillinger i kø.</p>
                <div class="d-flex align-items-center">
                  <input type="range" class="form-range me-3 green-slider" v-model.number="earliestPickupTime" min="5" max="55" step="5" :style="sliderStyle(55)">
                  <span class="badge green-badge">{{ earliestPickupTime }} min</span>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold">Maks hentetid (minutter)</label>
                <p class="text-muted small">Hentetiden vil aldri overstige denne verdien, uansett hvor mange bestillinger som er i kø.</p>
                <div class="d-flex align-items-center">
                  <input type="range" class="form-range me-3 green-slider" v-model.number="maxPickupTime" min="20" max="120" step="5" :style="sliderStyle(120)">
                  <span class="badge green-badge">{{ maxPickupTime }} min</span>
                </div>
              </div>

              <div class="mt-3">
                <label class="form-label fw-bold">Kjøkkenkapasitet (kr per minutt)</label>
                <p class="text-muted small">Hvor mange kroner med mat kjøkkenet kan produsere per minutt. Lavere tall = lengre hentetid ved mange bestillinger.</p>
                <div class="d-flex align-items-center">
                  <input type="range" class="form-range me-3 green-slider" v-model.number="krPerMinute" min="25" max="500" step="25" :style="sliderStyle(500)">
                  <span class="badge green-badge">{{ krPerMinute }} kr/min</span>
                </div>
              </div>

              <!-- Example calculation to help merchant understand -->
              <div class="mt-3 p-2 border rounded bg-body">
                <p class="small mb-1 fw-bold">Eksempel: Ordre på 1500 kr, ingen bestillinger i kø</p>
                <p class="small mb-0 text-muted">
                  Hentetid: <strong>{{ examplePickupTime(1500, 0) }} min</strong>
                  (ordren tar {{ Math.floor(1500 / krPerMinute) }} min å tilberede)
                </p>
                <p class="small mb-1 mt-2 fw-bold">Eksempel: Ordre på 1500 kr, kø verdi 2500 kr</p>
                <p class="small mb-0 text-muted">
                  Hentetid: <strong>{{ examplePickupTime(1500, 2500) }} min</strong>
                </p>
              </div>

              <div v-if="isSuperAdmin()" class="mt-3">
                <label class="form-label fw-bold">Checkout intent-vekt</label>
                <p class="text-muted small">Hvor mye vekt som legges på brukere som er i checkout men ikke har bestilt ennå. 0.8 betyr at 80% av verdien telles med i køberegningen.</p>
                <div class="d-flex align-items-center">
                  <input type="range" class="form-range me-3 green-slider" v-model.number="intentWeight" min="0" max="1" step="0.1" style="max-width: 220px;">
                  <span class="badge green-badge">{{ Math.round(intentWeight * 100) }}%</span>
                </div>
              </div>

              <div class="mt-3">
                <label class="form-label fw-bold">Dine egne notater for å hjelpe deg å finne riktig verdi for kjøkkenkapasitet</label>
                <textarea class="form-control notes-textarea" rows="5" v-model="smartPickupTimeNotes" placeholder="F.eks. 500 kr/min med 3 kokker, 400 kr/min med 2 kokker..."></textarea>
              </div>

            </div>
          </div>

          <div class="mt-4 mb-5">
            <button type="button" class="bo_btn" style="width: 155px;" @click="save()">Lagre</button>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import MerchantUtil from "@/util/MerchantUtil";
import SettingsService from "@/services/settings/SettingsService";
import LocationUtil from "@/util/LocationUtil";
import LocationService from "@/services/location/LocationService";
import UserUtil from "@/util/UserUtil";

export default {
    components: {LoadingSpinner},
    data() {
      return {
        loading: false,
        locationId: '',
        locations: [],
        earliestPickupTime: 0,
        useManualPickupOrDeliveryTime: false,
        krPerMinute: 100,
        maxPickupTime: 90,
        intentWeight: 0.8,
        smartPickupTimeNotes: ''
      };
    },
    watch: {
      locationId(newLocationId) {
        let location = this.getLocation(newLocationId);
        if (location) {
          this.earliestPickupTime = location.earliestPickupTime;
          this.useManualPickupOrDeliveryTime = location.useManualPickupOrDeliveryTime || false;
          this.krPerMinute = location.smartPickupTimeKrPerMinute || 100;
          this.maxPickupTime = location.smartPickupTimeMaxMinutes || 90;
          this.intentWeight = location.smartPickupTimeIntentWeight !== undefined ? location.smartPickupTimeIntentWeight : 0.8;
          this.smartPickupTimeNotes = location.smartPickupTimeNotes || '';
        }
      },
      earliestPickupTime(newValue) {
        // Sliding "Minimums hentetid" up pushes "Maks hentetid" up so it stays at least 5 min above
        // (the Maks slider keeps a fixed min, so its thumb visibly slides right when this fires).
        if (this.maxPickupTime < newValue + 5) {
          this.maxPickupTime = newValue + 5;
        }
      },
      maxPickupTime(newValue) {
        // Sliding "Maks hentetid" down pulls "Minimums hentetid" down to keep the 5 min gap (floor 5).
        if (newValue < this.earliestPickupTime + 5) {
          this.earliestPickupTime = Math.max(5, newValue - 5);
        }
      }
    },
    computed: {
      pickupTimeOptions(){
        let options = [];
        for (let i = 5; i <= 120; i += 5) {
          options.push(i);
        }
        return options;
      },
      activateSaveButton(){
        if(this.earliestPickupTime > 0){
          return true;
        } else {
          return false;
        }
      }
    },
    mounted() {
      this.locationId = this.$route.query.locationId;

      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data;

            let location = this.getLocation(this.locationId);
            this.earliestPickupTime = location.earliestPickupTime;
            this.useManualPickupOrDeliveryTime = location.useManualPickupOrDeliveryTime || false;

            // Load smart pickup time settings from location (with defaults for existing locations)
            this.krPerMinute = location.smartPickupTimeKrPerMinute || 100;
            this.maxPickupTime = location.smartPickupTimeMaxMinutes || 90;
            this.intentWeight = location.smartPickupTimeIntentWeight !== undefined ? location.smartPickupTimeIntentWeight : 0.8;
            this.smartPickupTimeNotes = location.smartPickupTimeNotes || '';
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      isSuperAdmin(){
        return UserUtil.isSuperAdmin();
      },
      // Make each slider's length reflect its max value (longer track = bigger range), so a max-55
      // slider isn't the same length as a max-500 one. Clamped so small-max sliders stay usable and
      // the largest still fits the panel (the maxes span 1..500, so it can't be strictly linear).
      sliderStyle(maxValue){
        const px = Math.min(460, Math.max(110, maxValue * 2.2));
        return { maxWidth: px + 'px' };
      },
      getLocation(locationId){
        for (let i = 0; i < this.locations.length; i++) {
          let location = this.locations[i];
          if(location.id == locationId){
            return location;
          }
        }
        return undefined;
      },
      /**
       * Example calculation shown in the UI to help merchant understand the smart pickup time formula.
       * orderPrepTime = orderValue / krPerMinute
       * effectivePickupTime = max(basePickupTime, orderPrepTime) + (queueValue / krPerMinute), capped at maxPickupTime, rounded up to nearest 5 min.
       */
      examplePickupTime(orderValue, queueValue) {
        let orderPrepTime = Math.floor(orderValue / this.krPerMinute);
        let effectiveBase = Math.max(this.earliestPickupTime, orderPrepTime);
        let effective = effectiveBase + Math.floor(queueValue / this.krPerMinute);
        effective = Math.min(effective, this.maxPickupTime);
        // Round up to nearest 5 minutes
        effective = Math.ceil(effective / 5) * 5;
        return effective;
      },
      save(){
        this.loading = true;
        let smartSettings = {
          smartPickupTimeKrPerMinute: this.krPerMinute,
          smartPickupTimeMaxMinutes: this.maxPickupTime,
          smartPickupTimeIntentWeight: this.intentWeight,
          smartPickupTimeNotes: this.smartPickupTimeNotes
        };
        this.changeEarliestPickupTime(MerchantUtil.getMerchant(), this.locationId, this.earliestPickupTime, smartSettings);
      },
      setEarliestPickupTime(minutes){
        this.earliestPickupTime = minutes;
        this.loading = true;
        this.changeEarliestPickupTime(MerchantUtil.getMerchant(), this.locationId, minutes, null);
      },
      changeEarliestPickupTime(merchant, locationId, earliestPickupTime, smartSettings){
        SettingsService.changeEarliestPickupTime(merchant, locationId, earliestPickupTime, smartSettings).then(
            (response) => {
              let documentId = response.data;
              if(documentId){
                this.$router.push({ path: 'overview' });
              } else {
                console.log("changeEarliestPickupTime failed");
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
            }
        );
      },
      formatPickupTime(minutes){
        if (minutes < 60) {
          return minutes + ' min';
        }
        let hours = Math.floor(minutes / 60);
        let remaining = minutes % 60;
        let timeWord = hours >= 2 ? 'timer' : 'time';
        if (remaining === 0) {
          return hours + ' ' + timeWord;
        }
        return hours + ' ' + timeWord + ' ' + remaining + ' min';
      },
      isCurrent(minutes){
          return (minutes == this.earliestPickupTime);
      }
    }
  };
</script>

<style scoped>
.notes-textarea::placeholder {
  color: #78b13f;
  opacity: 0.7;
  font-style: italic;
}
.green-badge {
  background-color: #78b13f;
}
.green-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  background-color: #78b13f;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  margin-top: -10px;
}
.green-slider::-moz-range-thumb {
  background-color: #78b13f;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
}
.green-slider::-webkit-slider-runnable-track {
  background-color: #c8ddb3;
  height: 8px;
  border-radius: 4px;
}
.green-slider::-moz-range-track {
  background-color: #c8ddb3;
  height: 8px;
  border-radius: 4px;
}
</style>
